import React from 'react';
import './CreateJobAdvanced.scss';
import { createJobConfigHooks } from './hooks';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { NotificationsCard } from './components/NotificationsCard';
import { JobDetailsCard } from './components/JobDetailsCard';
import { ScheduleCard } from './components/ScheduleCard';

const DEFAULT_CONFIG = {
  kronos: {
    type: "1password_item",
    "1password_item_path": "Client credentials/UKG - EnjoyWurkAdmin - apiuser"
  },
  sftp: {
    type: "1password_item",
    "1password_item_path": "Client credentials/Empower 401k SFTP"
  },
  emailConfig: {
    emailContacts: [],
    sendSuccessEmails: "true",
  },
  slackSimplified: true,
  privateKey: {
    type: "1password_item",
    "1password_item_path": "Client credentials/Wurkforce empower private key"
  },
  sourceDirectory: "/Home/wurk/From_Empower",
  archiveDirectory: "/Home/wurk/From_Empower/archive",
}

const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({partner: 'Empower', defaultConfig: DEFAULT_CONFIG});

const ClientPlanCard = () => {
  const [companyName, setCompanyName] = useJobConfig(["companyName"]);
  const [planNumber, setPlanNumber] = useJobConfig(["planNumber"]);
  const [earningsList, setEarningsList] = useJobConfig(["earningListName"]);
  const [searchCriteria, setSearchCriteria] = useJobConfig(["searchCriteriaToUpload"]);
  return <Card>
    <CardHeader title="Client & Plan" subheader="Identifies which client this feed is for" />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="comp_short_name"
            label="Company short name"
            variant="standard"
            helperText="Internal client identifier"
            required
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Plan Number"
            variant="standard"
            helperText="From Empower paperwork"
            required
            value={planNumber}
            onChange={e => setPlanNumber(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Earnings list name"
            variant="standard"
            helperText="Defaults to '401k Earnings List'"
            value={earningsList}
            onChange={e => setEarningsList(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="UKG company ID"
            required
            variant="standard"
            helperText="The UKG company ID for uploading the files"
            type="number"
            value={searchCriteria?.company_id ?? ""}
            onChange={e => {
              const raw = e.target.value;
              if (raw === "") {
                setSearchCriteria(undefined);
                return;
              }
              const asNumber = Number(raw);
              if (!Number.isFinite(asNumber)) return;
              setSearchCriteria({ company_id: asNumber });
            }}
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
}

const SftpDirectoriesCard = () => {
  const [sourceDirectory] = useJobConfig(["sourceDirectory"]);
  const [archiveDirectory] = useJobConfig(["archiveDirectory"]);
  return <Card>
    <CardHeader title="SFTP Directories" subheader="Set automatically, shown here for reference only." />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Source directory"
            variant="standard"
            helperText="The job reads incoming files from this folder"
            required
            value={sourceDirectory || ""}
            disabled
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Archive directory"
            variant="standard"
            helperText="The job moves the files here after a successful import"
            required
            value={archiveDirectory || ""}
            disabled
            onChange={() => {}}
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
}

const DeductionMappingCard = () => {
  const [_mapping, setMapping] = useJobConfig(["deductionMappings"]);
  const mapping = _mapping || [];
  function addRow() {
    setMapping(
      [
        ...mapping,
        {
          "name": "",
          "percentKey": "",
          "amountKey": ""
        }
      ]
    )
  }

  function deleteRow(i) {
    setMapping(mapping.filter((_, index) => index !== i))
  }

  function replaceItem(i, newItem) {
    const newMapping = mapping.map((item, index) => {
      if(index === i) {
        return newItem
      } else {
        return item
      }
    })
    setMapping(newMapping)
  }
  function setItemField(i, field, newvalue) {
    replaceItem(i, {
      ...mapping[i],
      [field]: newvalue
    })
  }
  return <Card>
    <CardHeader title="Deduction mapping" subheader="Maps each deduction to its Empower percent and amount columns. One row per deduction." />
    <CardContent>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Deduction name</TableCell>
            <TableCell>Percent key</TableCell>
            <TableCell>Amount key</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {
            mapping.map((row, i) => {
              return <TableRow key={i}>
                <TableCell>
                  <TextField fullWidth label="Deduction name" variant="standard" placeholder="e.g. 401k" required value={row.name} onChange={(e) => setItemField(i, "name", e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth label="Percent key" variant="standard" placeholder="e.g. Before Tax Deduction Percent" required value={row.percentKey} onChange={(e) => setItemField(i, "percentKey", e.target.value)} />
                </TableCell>
                <TableCell>
                  <TextField fullWidth label="Amount key" variant="standard" placeholder="e.g. Before Tax Deduction Amount" required value={row.amountKey} onChange={(e) => setItemField(i, "amountKey", e.target.value)} />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => deleteRow(i)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>

            })
          }
        </TableBody>
      </Table>
      <Button startIcon={<AddIcon />} variant='outlined' onClick={addRow}>
        Add row
      </Button>
    </CardContent>
  </Card>
}

export const EmpowerImportForm = () => {
  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
    </Grid>
    <Grid item xs={12}>
      <ClientPlanCard />
    </Grid>
    <Grid item xs={12}>
      <SftpDirectoriesCard />
    </Grid>
    <Grid item xs={12}>
      <DeductionMappingCard />
    </Grid>
    <Grid item xs={12}>
      <NotificationsCard useJobConfig={useJobConfig} />
    </Grid>
    <Grid item xs={12}>
      <ScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
    </Grid>
  </Grid>
}
